import {isAddress, type Address} from 'viem';
import {DEFAULT_RPC_URL, ROBINHOOD_CHAIN_ID, parseDeployment, type Deployment} from '@colosseum/shared';

/**
 * Where the site gets its data, and where it sends transactions. The two are
 * deliberately separate.
 *
 * Data comes from the indexer, which is a cache: it may be down, stale or wrong
 * without that costing anyone anything. The addresses a transaction is sent to
 * are baked into this bundle at build time from a file in `deployments/` and
 * are never taken from the network, because an API that could name the factory
 * or a token's curve could point every launch, trade, claim and draw at
 * contracts of its choosing. `/api/config` is read only to confirm the indexer
 * serves the same deployment as this build; on any disagreement the site says
 * so and sends nothing.
 */

export interface Addresses {
  factory: Address;
  arena: Address;
  ledger: Address;
  locker: Address;
  router: Address;
  poolManager: Address;
  quoter: Address | null;
  universalRouter: Address | null;
}

export interface SiteConfig {
  apiUrl: string;
  rpcUrl: string;
  chainId: number;
  chainName: string;
  explorerUrl: string | null;
  /** Transaction targets, from the deployment file baked in at build time. Null when none is. */
  addresses: Addresses | null;
  /** Set when the indexer reports a different chain or deployment than this build. Every action is disabled while it is. */
  mismatch: string | null;
}

const env = import.meta.env;

/** Only an http(s) URL may become an RPC, an explorer link or a chain-add request. Anything else is dropped. */
export function httpUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  try {
    const u = new URL(value);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    return u.toString().replace(/\/$/, '');
  } catch {
    return null;
  }
}

/**
 * The deployment file chosen at build time (see `selectDeployment` in
 * `vite.config.ts`), injected as a constant so the bundle carries that one
 * file and nothing else from `deployments/`.
 */
function bakedDeployment(): {name: string; deployment: Deployment} | null {
  const injected = __DEPLOYMENT__;
  if (!injected) return null;
  try {
    return {name: injected.name, deployment: parseDeployment(injected.raw)};
  } catch (e) {
    console.error(`deployments/${injected.name} is not a valid deployment: ${(e as Error).message}`);
    return null;
  }
}

const baked = bakedDeployment();
const d = baked?.deployment;

export const bakedDeploymentName: string | null = baked?.name ?? null;

export const config: SiteConfig = {
  apiUrl: (env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://127.0.0.1:8787',
  rpcUrl: httpUrl(env.VITE_RPC_URL) ?? DEFAULT_RPC_URL,
  chainId: d?.chainId ?? ROBINHOOD_CHAIN_ID,
  chainName: d?.name ?? 'Robinhood Chain',
  explorerUrl: httpUrl(env.VITE_EXPLORER_URL) ?? httpUrl(d?.explorerUrl) ?? null,
  addresses: d
    ? {
        factory: d.factory,
        arena: d.arena,
        ledger: d.ledger,
        locker: d.locker,
        router: d.router,
        poolManager: d.poolManager,
        quoter: d.quoter ?? null,
        universalRouter: d.universalRouter ?? null,
      }
    : null,
  mismatch: null,
};

const CHECKED = ['factory', 'arena', 'ledger', 'locker', 'router'] as const;

/**
 * Asks the indexer which deployment it serves and checks it against this build.
 * The answer never supplies an address: it can only confirm or contradict the
 * ones baked in. It may supply an explorer URL the build lacks, and, in
 * development only, the RPC of the fork it is reading.
 */
export async function loadRemoteConfig(): Promise<void> {
  try {
    const res = await fetch(`${config.apiUrl}/api/config`, {signal: AbortSignal.timeout(5000)});
    if (!res.ok) return;
    const body = (await res.json()) as {
      chainId?: unknown; rpcUrl?: unknown; explorerUrl?: unknown; addresses?: Record<string, unknown> | null;
    };
    if (!config.explorerUrl) config.explorerUrl = httpUrl(body.explorerUrl);
    // A production bundle reads the chain through the RPC it was built with; a
    // browser cannot reach an RPC bound to the indexer's loopback anyway.
    if (env.DEV && !env.VITE_RPC_URL) {
      const rpc = httpUrl(body.rpcUrl);
      if (rpc) config.rpcUrl = rpc;
    }

    const problems: string[] = [];
    if (typeof body.chainId === 'number' && body.chainId !== config.chainId) {
      problems.push(`chain ${body.chainId} where this build is for ${config.chainId}`);
    }
    if (config.addresses && body.addresses) {
      for (const key of CHECKED) {
        const theirs = body.addresses[key];
        const ours = config.addresses[key];
        if (typeof theirs === 'string' && isAddress(theirs) && theirs.toLowerCase() !== ours.toLowerCase()) {
          problems.push(`a ${key} at ${theirs} where this build has ${ours}`);
        }
      }
    }
    config.mismatch = problems.length ? `The indexer reports ${problems.join(', and ')}.` : null;
    if (config.mismatch) console.error(config.mismatch);
  } catch {
    // Offline or no indexer. The site says so in the header.
  }
}

/** True when a transaction may be built: addresses are baked in and the indexer does not contradict them. */
export function canAct(): boolean {
  return config.addresses !== null && config.mismatch === null;
}

/** A link to the explorer, or null while no explorer is configured or the value is not an address or hash. */
export function explorerLink(kind: 'tx' | 'address' | 'token', value: string): string | null {
  if (!config.explorerUrl) return null;
  if (!/^0x[0-9a-fA-F]{40}$/.test(value) && !/^0x[0-9a-fA-F]{64}$/.test(value)) return null;
  return `${config.explorerUrl}/${kind}/${value}`;
}
